<template>
  <div class="timeline-wrap">
    <div class="timeline-box" ref="timelineBox">
      <div class="timeline" :style="{ width: `${timelineSvgWidth}px` }">
        <svg class="timeline-svg" :width="timelineSvgWidth" :height="100">
          <line v-for="n of 5" :key="`guide-${n}`"
            class="grid-line"
            :class="{ baseline: n === 5 }"
            :x1="0"
            :y1="(n - 1) * 25"
            :x2="timelineSvgWidth"
            :y2="(n - 1) * 25"/>
          <path
            v-for="(item, index) in timelineSvgPathItems" :key="`path-${index}`"
            class="timeline-path"
            :stroke-width="2"
            :d="item.d"/>
        </svg>
        <template v-if="activeIndex !== null">
          <div class="active-indicator"
            :style="{ left: `${activeIndicatorLeft}px` }">
            <span :style="activeIndicatorCircleStyle"></span>
          </div>
        </template>
        <button v-for="(item, index) in nodeItems" :key="index"
          class="timeline-item"
          :class="eventItemsCssClassData[item.index]"
          :style="{ left: `${index * timelineItemWidth + timelineLeftPadding}px` }"
          @click="$emit('setActiveItem', item)">
          <span class="text">
            <template v-if="item.type === 'active'">A</template>
            <template v-else-if="item.type === 'inactive'">I</template>
            <template v-else>{{ item.nodeData.order }}</template>
          </span>
        </button>
      </div>
    </div>
    <span v-for="(duration, index) in timelineValuesY" :key="duration"
      class="timeline-y"
      :style="{ top: `${index * 25}px` }">
      {{ duration }}
    </span>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import AnimateScroll from '@/libs/animate-scroll'

export default {
  name: 'FunnelSessionTimeline',
  props: [
    'maxChartDuration',
    'timelineValuesY',
    'timelineSvgPathItems',
    'activeIndex',
    'nodeItems',
    'eventItemsCssClassData',
    'timelineItemWidth',
    'timelineLeftPadding'
  ],
  data () {
    return {
      timelineBoxWidth: 0
    }
  },
  computed: {
    ...mapState([
      'resizeTimestamp'
    ]),
    timelineSvgWidth () {
      const min = this.timelineBoxWidth
      const rightPadding = 50
      return Math.max(min, this.timelineSvgPathItems.length * this.timelineItemWidth + rightPadding)
    },
    activeIndicatorCircleStyle () {
      if (this.activeIndex === null) {
        return null
      }

      const item = this.nodeItems.find(item => item.index === this.activeIndex)
      let percentage = 0

      if (item.duration && this.maxChartDuration) {
        const maxDuration = this.maxChartDuration

        if (maxDuration > 0) {
          percentage = item.duration / maxDuration * 100
        }
      }

      return {
        top: `${100 - percentage}%`
      }
    },
    activeIndicatorLeft () {
      if (this.activeIndex === null) {
        return null
      }

      const item = this.nodeItems.find(item => item.index === this.activeIndex)
      return item.nodeData.order * this.timelineItemWidth
    }
  },
  watch: {
    resizeTimestamp (value) {
      this.setTimelineWidth()
    },
    activeIndicatorLeft (value) {
      const el = this.$refs.timelineBox

      if (!value || !el) {
        return
      }

      if (!this.scroller) {
        this.scroller = new AnimateScroll(this.$refs.timelineBox)
      }

      const halfWidth = el.offsetWidth / 2
      this.scroller.scrollTo({ x: value - halfWidth })
    }
  },
  mounted () {
    this.setTimelineWidth()
  },
  methods: {
    setTimelineWidth () {
      const el = this.$refs.timelineBox

      if (el) {
        this.timelineBoxWidth = el.offsetWidth
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.timeline-wrap {
  position: relative;
  padding-left: 114px;

  .timeline-y {
    position: absolute;
    width: 103px;
    left: 0px;
    margin-top: -6px;
    text-align: right;
    font-size: 12px;
    color: #444;
  }

  .timeline-box {
    height: 161px;
    margin-right: 114px;
    overflow-x: auto;
    overflow-y: hidden;
  }

  .grid-line {
    stroke-width: 1px;
    stroke: #e6e6e6;
    &.baseline {
      stroke: #767676;
    }
  }

  .timeline {
    position: relative;

    .timeline-item {
      box-sizing: border-box;
      position: absolute;
      top: 121px;
      left: 0;
      width: 24px;
      height: 24px;
      margin-left: -12px;
      margin-top: -12px;
      border-radius: 50%;

      .text {
        @include positionXYcenter;
        margin-top: 1px;
        color: #fff;
        font-size: 12px;
        font-weight: 600;
      }

      &.active {
        box-shadow: 0 6px 5px -2px rgba(0, 0, 0, 0.15);
      }
      &.node-active,
      &.node-inactive {
        border-radius: 5px;
      }

      &.node-active {
        border: solid 1px #d7d7d7;
        background-color: #ffffff;
        .text {
          color: rgba(0, 0, 0, 0.7);
        }
      }
      &.node-inactive {
        background-color: #1b1b1b;
      }
      &.node-hover {
        background-color: #289eef;
      }
      &.node-click {
        background-color: #ffd738;
      }
      &.node-hover-click {
        background-color: #ab85ff;
      }
      &.node-click,
      &.node-scroll-up,
      &.node-scroll-down {
        .text {
          color: rgba(0, 0, 0, 0.7);
        }
      }

      &.node-scroll-up,
      &.node-scroll-down {
        border: solid 1px #d7d7d7;
      }
      &.node-scroll-up.phone {
        background-color: #ff8400;
        .text {
          color: #fff;
        }
      }
    }

    .active-indicator {
      position: absolute;
      top: 0;
      width: 1px;
      margin-left: -6px;
      height: 100px;
      background-color: #ec0047;

      &> span {
        position: absolute;
        box-sizing: border-box;
        width: 5px;
        height: 5px;
        left: 0;
        margin-left: -2.5px;
        margin-top: -2.5px;
        border-radius: 50%;
        border: 2px solid #ec0047;
        background-color: #fff;
      }
    }
  }
}

.timeline-path {
  fill: transparent;
  stroke: #c1c1c1;
}
</style>
